import { QueryClient } from '@tanstack/react-query';
import type { Provider } from '@urbanx/agx.crm.client';
import { UploadedDocument } from '@urbanx/agx-ui-components';
import { AutopilotTaskDto, SearchTaskRequestTaskDue } from '@urbanx/agx.activities.client';
import {
  DocumentTypes,
  UploadedDocument as CampaignUploadedDocument,
} from '@urbanx/agx.campaigns.client';

export enum ConnectionState {
  NotConnected,
  Connecting,
  Connected,
  Reconnecting,
  Disconnected,
}

export enum NotificationType {
  PdfProcessedEvent = 'PdfProcessedEvent',
  ReadyForDocuSignEvent = 'ReadyForDocuSignEvent',
  CampaignUpdatedEvent = 'CampaignUpdatedEvent',
  ContractUpdatedEvent = 'ContractUpdatedEvent',
  RexListingCreatedEvent = 'RexListingCreatedEvent',
  CrmListingCreatedEvent = 'CrmListingCreatedEvent',
  ContractStageUpdatedEvent = 'ContractStageUpdatedEvent',
  TaskUpdatedNotification = 'TaskUpdatedNotification',
  TaskCreatedNotification = 'TaskCreatedNotification',
  TaskBecameVisibleNotification = 'TaskBecameVisibleNotification',
  TaskAssigneeChangedNotification = 'TaskAssigneeChangedNotification',
  TaskDeletedNotification = 'TaskDeletedNotification',
  TaskCollectionChangedNotification = 'TaskCollectionChangedNotification',
  WorkflowConcurrencyNotification = 'WorkflowConcurrencyNotification',
  ContractDocumentUpdatedNotification = 'ContractDocumentUpdatedNotification',
  AutomationFailureNotification = 'AutomationFailureNotification',
}

export interface PushNotification {
  registerEvent: (
    eventType: string,
    callback: (event: NotificationEvents, queryClient: QueryClient) => void,
    id?: (string | undefined)[]
  ) => void;
  unregisterEvent: (
    eventType: string,
    eventCallback: (event: NotificationEvents) => void,
    id?: (string | undefined)[]
  ) => void;
}

export interface PdfProcessedEvent {
  sessionId?: string | null;
  campaignId: string;
  formId: string;
  uploadedDocument: UploadedDocument;
}

export interface CampaignUpdatedEvent {
  campaignId: string;
  agencyId: string;
}

export interface ContractUpdatedEvent {
  contractId: string;
  campaignId: string;
  agencyId: string;
}

export interface RexListingCreatedEvent {
  campaignId: string;
  agencyId: string;
  rexListingId: number;
}

export interface CrmListingCreatedEvent {
  campaignId: string;
  agencyId: string;
  provider: Provider;
  listingId: string;
}

export interface ContractStageUpdatedEvent {
  campaignId: string;
  agencyId: string;
  contractId: string;
}

export interface TaskUpdatedNotification {
  task: AutopilotTaskDto;
  collectionStateChanged: boolean;
}

export interface TaskArrivalNotification {
  taskId: string;
  campaignId: string;
  agencyId: string;
  state?: NonNullable<AutopilotTaskDto['category']>['state'];
  stage: NonNullable<NonNullable<AutopilotTaskDto['category']>['stage']>;
  teamType: NonNullable<AutopilotTaskDto['teamType']>;
  assignedTo?: string;
  isStaffVisible: boolean;
  taskDueFilters: SearchTaskRequestTaskDue[];
}

export interface TaskAssigneeChangedNotification extends TaskArrivalNotification {
  previousAssignedTo?: string;
}

export interface TaskDeletedNotification {
  taskId: string;
  campaignId: string;
  contractId?: string;
}

export interface TaskCollectionChangedNotification {
  campaignId: string;
  contractId?: string;
}

export interface WorkflowConcurrencyNotification {
  campaignId: string;
  contractId?: string;
}

export interface ContractDocumentUpdatedNotification {
  campaignId: string;
  contractId: string;
  documentType: DocumentTypes;
  document: CampaignUploadedDocument;
}

export interface AutomationFailureNotification {
  reference: string;
  agencyId: string | null;
  campaignId: string | null;
  campaignAddress: string | null;
  automationName: string;
}

export type NotificationEvents = PdfProcessedEvent &
  CampaignUpdatedEvent &
  ContractUpdatedEvent &
  RexListingCreatedEvent &
  CrmListingCreatedEvent &
  ContractStageUpdatedEvent &
  TaskUpdatedNotification &
  TaskArrivalNotification &
  TaskAssigneeChangedNotification &
  TaskDeletedNotification &
  TaskCollectionChangedNotification &
  WorkflowConcurrencyNotification &
  ContractDocumentUpdatedNotification &
  AutomationFailureNotification;
